import React, { useContext, useState, useEffect } from "react";
import { Modal, Box, Typography, Grid, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import NeoButton from "../NeoButton";
import { updateDoc, doc, getDocs, collection } from "firebase/firestore";
import { db } from "../../@shared/firebase/config";
import { useAuthStore } from "../../@shared/store/useAuthStore";
import { MainContext } from "../../App";

const containerVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const itemVariants = (i) => ({
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { delay: 0.05 * i } },
});

const AvatarSelectorModal = ({ open, onClose }) => {
  const { userData, setUserData } = useAuthStore();
  const { Theme } = useContext(MainContext);
  const [selectedAvatar, setSelectedAvatar] = useState(
    userData?.avatar?.name || ""
  );
  const [avatarList, setAvatarList] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, "users", userData.id, "avatars")
        );
        const avatars = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAvatarList(avatars);
      } catch (err) {
        console.error("Error fetching avatars:", err);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchAvatars();
    }
  }, [open]);

  const handleSelectAvatar = async (avatar) => {
    try {
      const newAvatar = {
        name: avatar.name,
        image: avatar.image,
        ws: avatar.ws,
      };

      const userRef = doc(db, "users", userData.id);
      await updateDoc(userRef, { avatar: newAvatar });
      setUserData((prev) => ({ ...prev, avatar: newAvatar }));
      onClose();
    } catch (err) {
      console.error("Error selecting avatar:", err);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        sx={{
          position: "absolute",
          top: "50%",
          left: { xs: "0%", md: "30%" },
          transform: "translate(-50%, -50%)",
          width: "90vw",
          maxWidth: 600,
          height: 400,
          bgcolor: Theme.palette.tirciary.main,
          borderRadius: 4,
          p: 3,
          overflow: "hidden",
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, color: "#fff", textAlign: "center" }}
        >
          Choose Your Avatar
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "70%",
            }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Grid
            container
            spacing={2}
            wrap="nowrap"
            sx={{
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {avatarList?.map((avatar, i) => (
              <Grid item key={avatar.name}>
                <motion.div
                  variants={itemVariants(i)}
                  initial="hidden"
                  animate="visible"
                >
                  <Box
                    onClick={() => {
                      setSelectedAvatar(avatar.name);
                      handleSelectAvatar(avatar);
                    }}
                    sx={{
                      cursor: "pointer",
                      border:
                        selectedAvatar === avatar?.name
                          ? "2px solid #e84057"
                          : "2px solid transparent",
                      borderRadius: 2,
                      overflow: "hidden",
                      transition: "0.2s",
                      backgroundColor: "#222",
                    }}
                  >
                    <img
                      src={avatar.image}
                      alt={avatar.name}
                      style={{
                        width: 100,
                        height: 140,
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: "14px",
                        textAlign: "center",
                        mt: 1,
                      }}
                    >
                      {avatar.name}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}

        <Box sx={{ textAlign: "center", mt: 3 }}>
          <NeoButton
            text="Close"
            onClick={onClose}
            color={Theme.palette.primary.main}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default AvatarSelectorModal;
